import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import type { FaqSection, CreateFaqSectionInput } from '../../types';

interface SectionFormProps {
  open: boolean;
  section: FaqSection | null; // null = create mode
  onClose: () => void;
  onSave: (data: CreateFaqSectionInput) => Promise<void>;
}

const DEFAULT_COLOR = '#666666';

export function SectionForm({ open, section, onClose, onSave }: SectionFormProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(section?.name ?? '');
      setColor(section?.color ?? DEFAULT_COLOR);
      setDescription(section?.description ?? '');
      setError('');
    }
  }, [open, section]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), color, description: description.trim() || undefined });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{section ? 'Edit Section' : 'Add Section'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            required
          />
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              label="Colour"
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 80 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Hex value"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 120 }}
            />
          </Box>
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          {error && (
            <Box color="error.main" fontSize={13}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
